import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { NotesService } from '@app/infrastructure/core/services/api/notes.service';
import { UserStateService } from '@app/infrastructure/core/services/state/user-state.service';
import { WebsocketsService } from '@app/infrastructure/core/services/web-sockets.service';
import {
  INoteCreationDTO,
  INoteDTO,
  NoteDTO,
} from '@app/infrastructure/models/note';
import { BehaviorSubject, Observable } from 'rxjs';
import { delay, filter, map, withLatestFrom } from 'rxjs/operators';

@Component({
  selector: `app-edit-notes`,
  template: `<app-edit-notes-presentation
    [note]="note"
    (exit)="close()"
    [currentUser]="currentUser$ | async"
    (save)="handleSave($event)"
    [editable]="editable"
    (emitDeleteNote)="deleteNote($event)"
    (emitAutoSaveNote)="autoSaveNote($event)"
  ></app-edit-notes-presentation>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotesEditSmartComponent implements OnInit, OnDestroy {
  @Input() note?: INoteDTO;
  @Input() dayBlockId: number;
  @Input() studentAssignmentId: number;
  @Input() isNewNote: boolean = false;
  @Output() exit = new EventEmitter<INoteDTO | void>();
  @Output() emitDeleteNote = new EventEmitter<string>();
  @Output() emitDestroy = new EventEmitter<void>();
  @Output() emitSave = new EventEmitter<INoteDTO>();
  message: BehaviorSubject<string>;
  public studentId: string = '';

  currentUser$ = this.userStateService.getUserSession();

  editable = false;
  createdNote: INoteDTO;

  constructor(
    private userStateService: UserStateService,
    private notesService: NotesService,
    public websocketService: WebsocketsService,
  ) {}

  ngOnInit(): void {
    this.userStateService.getUserSession().subscribe((user) => {
      // if (this.note?.student?.id !== user.student?.id) this.note = null;
    });
    this.editable = !this.note;
    this.currentUser$.subscribe((user) => (this.studentId = user.student?.id));
  }

  ngOnDestroy(): void {
    this.emitDestroy.emit();
  }

  handleSave(note: NoteDTO) {
    const fullNote = {
      ...note,
      studentAssignmentId: this.studentAssignmentId,
      dayBlockId: this.dayBlockId,
    };
    const noteToSave = this.note || this.createdNote;

    if (this.note.id) {
      this.notesService
        .updateNote(noteToSave.id, fullNote, true)
        .subscribe(() => {
          Object.assign(this.note, {
            ...this.createdNote,
            ...fullNote,
          });
          this.emitSave.emit({
            ...this.createdNote,
            ...fullNote,
          });
        });
    } else {
      this.notesService
        .createNote(fullNote)
        .pipe(
          withLatestFrom(
            this.currentUser$.pipe(
              map(({ student }) => student),
              filter((v) => !!v),
            ),
          ),
        )
        .subscribe(([newNote, student]) => {
          Object.assign(this.note, {
            ...newNote,
            student,
          });
          this.emitSave.emit(this.note);
        });
    }
  }

  autoSaveNote(note: Omit<INoteCreationDTO, 'dayBlockId'>) {
    const fullNote = {
      ...note,
      studentAssignmentId: this.studentAssignmentId,
      dayBlockId: this.dayBlockId,
    };
    if (this.note.id) {
      const noteToSave = this.note || this.createdNote;

      if (fullNote.message.length) {
        this.notesService.updateNote(noteToSave.id, fullNote).subscribe();
        if (this.createdNote) {
          this.createdNote = { ...this.createdNote, ...fullNote };
          Object.assign(this.note, { ...this.createdNote });
        }
      }
    } else {
      this.notesService
        .createNote(fullNote)
        .pipe(
          withLatestFrom(
            this.currentUser$.pipe(
              map(({ student }) => student),
              filter((v) => !!v),
            ),
          ),
        )
        .subscribe(([newNote, student]) => {
          this.createdNote = { ...newNote, student };
          Object.assign(this.note, { ...this.createdNote });
        });
    }
  }

  close(): void {
    this.exit.emit(this.note);
  }

  deleteNote(noteId: number) {
    this.notesService
      .deleteNote(this.studentId, noteId)
      .subscribe(() => this.close());
  }
}
